/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Enable Match Booster in syncs", description = "Turn on Match Booster in supported destination syncs to send enriched identifiers for higher audience reach.", lastUpdatedDate = new Date(1787184000000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Enable Match Booster in syncs",
        description: "Turn on Match Booster in supported destination syncs to send enriched identifiers for higher audience reach.",
        lastUpdatedDate: "Thu Aug 20 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "How Match Booster works in a sync",
          "depth": 0,
          "id": "how-match-booster-works-in-a-sync"
        }, {
          "value": "Enable Match Booster in a sync",
          "depth": 0,
          "id": "enable-match-booster-in-a-sync"
        }, {
          "value": "Choose a targeting level",
          "depth": 0,
          "id": "choose-a-targeting-level"
        }, {
          "value": "Monitor match rates",
          "depth": 0,
          "id": "monitor-match-rates"
        }, {
          "value": "If Match Booster isn't sending identifiers",
          "depth": 0,
          "id": "if-match-booster-isnt-sending-identifiers"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      a: "a",
      p: "p",
      em: "em",
      hr: "hr",
      h2: "h2",
      ol: "ol",
      li: "li",
      img: "img",
      ul: "ul"
    }, _provideComponents(), props.components), {BtOnly} = _components;
    if (!BtOnly) _missingMdxReference("BtOnly", true);
    return _jsxs(_Fragment, {
      children: [_jsx(BtOnly, {
        featureName: "Match Booster",
        extraText: " as an add-on product."
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {}), _jsx(_components.th, {})]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Audience"
              })
            }), _jsx(_components.td, {
              children: "Marketers, platform admins"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Prerequisites"
              })
            }), _jsxs(_components.td, {
              children: ["Match Booster ", _jsx(_components.a, {
                href: "/match-booster/configure-model",
                children: "enabled and enriched on a model"
              }), ", and a sync to a ", _jsx(_components.a, {
                href: "/match-booster/overview#supported-destinations",
                children: "supported destination"
              })]
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.em, {
          children: "After Match Booster enrichment has completed on a model, enable it in syncs to send enriched identifiers to supported advertising destinations."
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "how-match-booster-works-in-a-sync",
        children: "How Match Booster works in a sync"
      }), "\n", _jsxs(_components.p, {
        children: ["When you enable Match Booster on a sync, Hightouch adds the matched identifiers from the model's enrichment to the data it sends to the ad platform. Before you begin, ", _jsx(_components.a, {
          href: "/match-booster/configure-model",
          children: "configure Match Booster on a model"
        }), " and wait for the initial enrichment to complete."]
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["You can only enable Match Booster on syncs to ", _jsx(_components.a, {
            href: "/match-booster/overview#supported-destinations",
            children: "supported\ndestinations"
          }), ". The toggle does\nnot appear for unsupported destinations."]
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "enable-match-booster-in-a-sync",
        children: "Enable Match Booster in a sync"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["Go to ", _jsx(_components.a, {
              href: "https://app.hightouch.com/syncs?from=docs",
              children: _jsx(_components.strong, {
                children: "Activation > Syncs"
              })
            }), ". Select an existing sync or create a new one."]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["Open the ", _jsx(_components.strong, {
              children: "Configuration"
            }), " tab."]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["In the field mapping section, turn on ", _jsx(_components.strong, {
              children: "Match Booster"
            }), "."]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["Choose a ", _jsx(_components.a, {
              href: "#choose-a-targeting-level",
              children: "targeting level"
            }), "."]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsx(_components.p, {
            children: "Review which identifiers Hightouch will send. Hightouch automatically selects the best available enriched identifiers for each destination. Some destinations allow you to remove specific identifiers if needed."
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["Select ", _jsx(_components.strong, {
              children: "Save changes"
            }), "."]
          }), "\n"]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "match-booster/mb-sync-configuration.png",
          alt: "Sync configuration with Match Booster enabled, showing the targeting level and enriched field mappings."
        })
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "You do not need to manually map enriched Match Booster fields. Hightouch\nautomatically sends the appropriate identifiers based on what the destination\nsupports."
        })
      }), "\n", _jsx(Tip, {
        children: _jsxs(_components.p, {
          children: ["If you just enabled Match Booster for the first time, wait for the initial\nenrichment to complete before running your sync. You can check enrichment\nstatus on the model's ", _jsx(_components.strong, {
            children: "Match Booster"
          }), " tab."]
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "choose-a-targeting-level",
        children: "Choose a targeting level"
      }), "\n", _jsx(_components.p, {
        children: "When you enable Match Booster on a sync, select a targeting level that matches your campaign goals:"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Targeting level"
            }), _jsx(_components.th, {
              children: "What it does"
            }), _jsx(_components.th, {
              children: "When to use it"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Person"
              })
            }), _jsx(_components.td, {
              children: "Targets individuals based on person-level identifier matching."
            }), _jsx(_components.td, {
              children: "Default for most campaigns. Use for precision targeting and suppression."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Household"
              })
            }), _jsx(_components.td, {
              children: "Expands targeting to all people and devices at the same residential address."
            }), _jsxs(_components.td, {
              children: ["Use when the product or service is used by the entire household (retail, streaming, automotive, telecom). Requires ", _jsx(_components.a, {
                href: "/match-booster/configure-model#select-expansion-options",
                children: "Household Expansion"
              }), " enabled on the model."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "CTV"
              })
            }), _jsx(_components.td, {
              children: "Targets consumers on connected TV devices."
            }), _jsxs(_components.td, {
              children: ["Use for CTV campaigns on Roku, LG, Samsung, and Vizio. Requires ", _jsx(_components.a, {
                href: "/match-booster/configure-model#select-expansion-options",
                children: "CTV Matching"
              }), " enabled on the model."]
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: "Targeting levels cannot be changed after the sync is enabled. To change the targeting level for an audience, create a new sync with the same configuration and set the targeting level before activating it."
      }), "\n", _jsxs(_components.p, {
        children: ["See ", _jsx(_components.a, {
          href: "/match-booster/targeting-levels",
          children: "Targeting levels"
        }), " for detailed guidance on person-level versus household-level matching."]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "monitor-match-rates",
        children: "Monitor match rates"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch surfaces match rates for syncs to paid advertising destinations, whether or not Match Booster is enabled."
      }), "\n", _jsx(_components.p, {
        children: "To view match rates for a sync:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Go to ", _jsx(_components.a, {
            href: "https://app.hightouch.com/syncs?from=docs",
            children: _jsx(_components.strong, {
              children: "Activation > Syncs"
            })
          }), "."]
        }), "\n", _jsx(_components.li, {
          children: "Select the sync you want to review."
        }), "\n", _jsxs(_components.li, {
          children: ["Open the ", _jsx(_components.strong, {
            children: "Overview"
          }), " tab."]
        }), "\n", _jsxs(_components.li, {
          children: ["In the right-hand panel, find ", _jsx(_components.strong, {
            children: "Matched users"
          }), " and click ", _jsx(_components.strong, {
            children: "See details"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "match-booster/mb-matched-users.png",
          alt: "The sync overview page with the Matched users section highlighted in the right-hand panel."
        })
      }), "\n", _jsx(_components.p, {
        children: "The details panel shows:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Audience health status and monitoring state"
        }), "\n", _jsx(_components.li, {
          children: "Number of matched users in the ad platform audience"
        }), "\n", _jsx(_components.li, {
          children: "A link to view your audience directly in the ad platform"
        }), "\n"]
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "Match rates can take up to 48 hours to stabilize after the initial sync,\ndepending on the ad platform. Allow time for results to settle before\nevaluating performance."
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "if-match-booster-isnt-sending-identifiers",
        children: "If Match Booster isn't sending identifiers"
      }), "\n", _jsx(_components.p, {
        children: "If a sync isn't sending enriched identifiers, check:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "The destination isn't supported."
          }), " The ", _jsx(_components.strong, {
            children: "Match Booster"
          }), " toggle only appears on syncs to ", _jsx(_components.a, {
            href: "/match-booster/overview#supported-destinations",
            children: "supported destinations"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "The model isn't enriched."
          }), " Match Booster must be ", _jsx(_components.a, {
            href: "/match-booster/configure-model",
            children: "configured on the model"
          }), " before you can enable it on a sync."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Enrichment hasn't finished."
          }), " Match Booster enriches the model on a schedule, and the first enrichment can take up to 72 hours. Confirm it has completed on the model's ", _jsx(_components.strong, {
            children: "Match Booster"
          }), " tab before running the sync."]
        }), "\n"]
      })]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
